import { getXmlEscaper, getChosenPropsMutationXml, reintroduceBackslashes } from './BlocklifyUtil'
import _ from 'lodash'
const xmlEscape = getXmlEscaper()

//
// Handlers here are functions taking care of the basic language structure, ie variable declarations, conditionals etc
// See the esprima project documentation regarding the structure of each syntax node: https://esprima.readthedocs.io
//
export default function(registerHandler) {
  registerHandler('IfStatement', (code, node, walk) => {
    // NOTE: if the else statement is an IfStatement (not a BlockStatement), then we need to include it in the controls_if block
    let ifs = []
    while (node && node.type === 'IfStatement') {
      ifs.push({
        test: walk.expr(code, node.test),
        do: walk.stmt(code, node.consequent)
      })
      node = node.alternate
    }
    // node is now = node.alternate, ie the else part (at the end of the 'else if's)
    let elseBlock = node ? `<statement name="ELSE">${walk.stmt(code, node)}</statement>` : ``
    let ifBlocks = ifs.map(
      (c, index) =>
        `<value name="IF${index}">${c.test}</value><statement name="DO${index}">${c.do}</statement>`
    )
    // Build the mutation element and put the pieces together
    let elseIfMutationAttr = ifBlocks.length > 1 ? ` elseif="${ifBlocks.length - 1}"` : ''
    let elseMutationAttr = elseBlock ? ` else="1"` : ''
    let mutationBlock =
      elseIfMutationAttr || elseMutationAttr
        ? `<mutation${elseIfMutationAttr}${elseMutationAttr}></mutation>`
        : ''
    return `<block type="controls_if">${mutationBlock}${ifBlocks.join('')}${elseBlock}</block>`
  })

  registerHandler('BinaryExpression', (code, node, walk) => {
    const operatorMap = {
      '<': 'LT',
      '<=': 'LTE',
      '>': 'GT',
      '>=': 'GTE',
      '==': 'EQ',
      '===': 'EQ',
      '!=': 'NEQ',
      '!==': 'NEQ',
      '-': 'MINUS',
      '+': 'PLUS',
      '*': 'MULTIPLY',
      '/': 'DIVIDE',
      '^': 'POWER'
    }
    let left = walk.expr(code, node.left)
    let right = walk.expr(code, node.right)
    let blockType = 'logic_compare'
    if (node.operator === '+') {
      blockType = 'javascript_untyped_add'
    } else if (['-', '*', '/', '^'].indexOf(node.operator) >= 0) {
      blockType = 'math_arithmetic'
    }
    if (!operatorMap[node.operator]) {
      return null
    }
    return `<block type="${blockType}"><field name="OP">${
      operatorMap[node.operator]
    }</field><value name="A">${left}</value><value name="B">${right}</value></block>`
  })

  function connectBlockSequence(blocks) {
    if (!blocks || blocks.length === 0) {
      return ''
    }
    let nestedNextBlocks = blocks[blocks.length - 1]
    for (var i = blocks.length - 2; i >= 0; i--) {
      // Iterate backwards and nest next block inside previous block in a <next> element so that they are connected blocks
      nestedNextBlocks = blocks[i].replace(/<\/block>$/, `<next>${nestedNextBlocks}</next></block>`)
    }
    return nestedNextBlocks
  }

  function blockstatementHandler(code, node, walk) {
    let statementBlocks = node.body.map(s => walk.stmt(code, s))
    return connectBlockSequence(statementBlocks)
  }
  registerHandler('BlockStatement', blockstatementHandler)
  registerHandler('Program', blockstatementHandler)

  registerHandler('ExpressionStatement', (code, node, walk) => {
    // Only allow CallExpression and AssignmentExpression to appear as statements - the rest get wrapped
    if (
      node.expression.type !== 'CallExpression' &&
      node.expression.type !== 'AssignmentExpression'
    ) {
      let expressionBlock = walk.expr(code, node.expression)
      return `<block type="javascript_expression_statement"><value name="ignoredInput">${expressionBlock}</value></block>`
    }
    return walk.stmt(code, node.expression)
  })

  registerHandler('CallExpression', (code, node, walk, isStatement) => {
    const fnNode = node.callee
    const firstArgIsEmptyString =
      node.arguments &&
      node.arguments.length > 0 &&
      node.arguments[0].type === 'Literal' &&
      node.arguments[0].value === ''
    // Special handling for ['foo', 42, '\n'].join('')
    if (
      fnNode.type === 'MemberExpression' &&
      fnNode.object.type === 'ArrayExpression' &&
      fnNode.property.type === 'Identifier' &&
      fnNode.property.name === 'join' &&
      firstArgIsEmptyString
    ) {
      const addXmls = fnNode.object.elements.map(
        (e, idx) => `<value name="ADD${idx}">${walk.expr(code, e)}</value>`
      )
      return `<block type="text_join"><mutation items="${addXmls.length}"></mutation>${addXmls.join(
        ''
      )}</block>`
    }
    // Special handling for the string initializer form String('foo') because the built-in text block produces it
    if (
      node.callee.type === 'Identifier' &&
      node.callee.name === 'String' &&
      node.arguments.length > 0 &&
      node.arguments[0].type === 'Literal'
    ) {
      return walk.expr(code, node.arguments[0])
    }
    const argumentBlocks = node.arguments.map(a => walk.expr(code, a))
    const wrappedArgumentBlocks = argumentBlocks.map(
      (ab, index) => `<value name="ITEM${index}">${ab}</value>`
    )
    const blockType = isStatement
      ? 'javascript_function_invocation'
      : 'javascript_function_invocation_return'
    const functionValueBlock = walk.expr(code, node.callee)
    return `<block type="${blockType}"><mutation count="${
      argumentBlocks.length
    }"></mutation><value name="function">${functionValueBlock}</value>${wrappedArgumentBlocks.join(
      ''
    )}</block>`
  })

  registerHandler('NewExpression', (code, node, walk, isStatement) => {
    const argumentBlocks = node.arguments.map(a => walk.expr(code, a))
    const wrappedArgumentBlocks = argumentBlocks.map(
      (ab, index) => `<value name="ITEM${index}">${ab}</value>`
    )
    const functionValueBlock = walk.expr(code, node.callee)
    return `<block type="javascript_object_new"><mutation count="${
      argumentBlocks.length
    }"></mutation><value name="function">${functionValueBlock}</value>${wrappedArgumentBlocks.join(
      ''
    )}</block>`
  })

  registerHandler('Literal', (code, node, walk) => {
    if (node.value === null) {
      return `<block type="javascript_literal_null"></block>`
    } else if (node.regex != null) {
      return `<block type="javascript_literal_regexp"><field name="PATTERN">${node.regex.pattern}</field><field name="FLAGS">${node.regex.flags}</field></block>`
    } else if (typeof node.value === 'string' || node.value instanceof String) {
      // For strings, we need to handle newlines
      const lines = node.value.split('\n')
      if (lines.length > 1) {
        // Use newline block to represent this
        const blocks = []
        let blockCounter = 0
        lines.forEach((line, idx) => {
          if (line)
            blocks.push(
              `<value name="ADD${blockCounter++}"><block type="text"><field name="TEXT">${line}</field></block></value>`
            )
          if (idx < lines.length - 1)
            blocks.push(
              `<value name="ADD${blockCounter++}"><block type="javascript_newline"></block></value>`
            )
        })
        if (blockCounter > 1) {
          return `<block type="text_join"><mutation items="${blockCounter}"></mutation>${blocks.join(
            ''
          )}</block>`
        } else {
          return `<block type="javascript_newline"></block>`
        }
      }
      return `<block type="text"><field name="TEXT">${xmlEscape(
        reintroduceBackslashes(node.value)
      )}</field></block>`
    } else if (node.value === true || node.value === false) {
      return `<block type="logic_boolean"><field name="BOOL">${(
        node.value + ''
      ).toUpperCase()}</field></block>`
    } else if (typeof node.value === 'number') {
      return `<block type="math_number"><field name="NUM">${node.value}</field></block>`
    } else {
      throw new Error('Unsupported literal ' + node.value)
    }
  })

  registerHandler('VariableDeclaration', (code, node, walk) => {
    let declaratorBlocks = node.declarations.map(d => walk.expr(code, d))
    let connectedDeclarators = connectBlockSequence(declaratorBlocks)
    return `<block type="javascript_variable_declaration"><statement name="declarations">${connectedDeclarators}</statement></block>`
  })

  registerHandler('VariableDeclarator', (code, node, walk) => {
    let id = node.id.name // Only in the event of es6 destructuring assignment is id not an identifier
    let initBlock = node.init ? `<value name="value">${walk.expr(code, node.init)}</value>` : ''
    return `<block type="javascript_variable_assign"><field name="variableName">${id}</field>${initBlock}</block>`
  })

  registerHandler('Identifier', (code, node, walk) => {
    return `<block type="javascript_variable_access"><field name="variableName">${node.name}</field></block>`
  })

  registerHandler('ObjectExpression', (code, node, walk) => {
    const elements = getChosenPropsMutationXml(code, node.properties, walk)
    return `<block type="javascript_object_literal">${elements.mutationElt.replace(
      /chosenprops/,
      'things'
    )}${elements.propElts.join('')}</block>`
  })

  registerHandler('AssignmentExpression', (code, node, walk) => {
    let right = walk.expr(code, node.right)
    if (node.operator === '=') {
      if (node.left.type === 'Identifier') {
        return `<block type="javascript_variable_assign"><field name="variableName">${node.left.name}</field><value name="value">${right}</value></block>`
      } else if (node.left && node.left.type === 'MemberExpression') {
        const objectBlock = walk.expr(code, node.left.object)
        const propertyValueBlock = walk.expr(code, node.right)
        if (!node.left.computed) {
          const propertyName = node.left.property.name || node.left.property.value // Assumes identifier/literal
          return `<block type="javascript_property_set"><value name="OBJECT">${objectBlock}</value><field name="PROPERTY">${propertyName}</field><value name="VALUE">${propertyValueBlock}</value></block>`
        } else {
          const propertyBlock = walk.expr(code, node.left.property)
          return [
            `<block type="javascript_computed_property_set">`,
            `<value name="OBJECT">${objectBlock}</value>`,
            `<value name="PROPERTY">${propertyBlock}</value>`,
            `<value name="VALUE">${propertyValueBlock}</value>`,
            `</block>`
          ].join('')
        }
      }
    }
    // TODO: other assignment types
  })

  function handleFunctionExpression(code, node, walk) {
    let paramNames = node.params.map(paramNode => {
      if (paramNode.type !== 'Identifier') {
        throw new Error('Unsupported function argument node ' + paramNode.type)
      }
      return paramNode.name
    })
    let bodyBlock = walk.stmt(code, node.body)
    return `<block type="javascript_lambda_declaration"><field name="arguments">${paramNames.join(
      ', '
    )}</field><statement name="body">${bodyBlock}</statement></block>`
  }

  registerHandler('FunctionExpression', handleFunctionExpression)

  registerHandler('FunctionDeclaration', (code, node, walk) => {
    if (node.id === null || node.id.type !== 'Identifier') {
      throw new Error('Unsupported function declaration without identifier')
    }
    const paramNames = node.params.map(paramNode => {
      if (paramNode.type !== 'Identifier') {
        throw new Error('Unsupported function argument node ' + paramNode.type)
      }
      return paramNode.name
    })
    const bodyBlock = walk.stmt(code, node.body)
    return `<block type="javascript_function_declaration"><field name="NAME">${
      node.id.name
    }</field><field name="arguments">${paramNames.join(
      ', '
    )}</field><statement name="body">${bodyBlock}</statement></block>`
  })

  registerHandler('WhileStatement', (code, node, walk) => {
    const testBlock = walk.expr(code, node.test)
    const bodyBlock = walk.stmt(code, node.body)
    return `<block type="controls_whileUntil"><field name="MODE">WHILE</field><value name="BOOL">${testBlock}</value><statement name="DO">${bodyBlock}</statement></block>`
  })

  registerHandler('ForStatement', (code, node, walk) => {
    let init = null
    if (node.init && node.init.type === 'VariableDeclaration') {
      let declaratorBlocks = node.init.declarations.map(d => walk.expr(code, d))
      init = connectBlockSequence(declaratorBlocks)
    } else {
      init = walk.expr(code, node.init)
    }
    const initBlock = init ? `<statement name="init">${init}</statement>` : ''
    const test = walk.expr(code, node.test)
    const testBlock = test ? `<value name="test">${test}</value>` : ''
    const body = walk.stmt(code, node.body)
    const bodyBlock = body ? `<statement name="body">${body}</statement>` : ''
    const update = walk.stmt(code, node.update)
    const updateBlock = update ? `<statement name="update">${update}</statement>` : ''
    return `<block type="javascript_for_loop">${initBlock}${testBlock}${bodyBlock}${updateBlock}</block>`
  })

  registerHandler('ConditionalExpression', (code, node, walk) => {
    const test = walk.expr(code, node.test)
    const consequent = walk.expr(code, node.consequent)
    const alternate = walk.expr(code, node.alternate)
    return `<block type="logic_ternary"><value name="IF">${test}</value><value name="THEN">${consequent}</value><value name="ELSE">${alternate}</value></block>`
  })

  registerHandler('ArrayExpression', (code, node, walk) => {
    let itemBlocks = node.elements.map(
      (e, index) => `<value name="ADD${index}">${walk.expr(code, e)}</value>`
    )
    return `<block type="lists_create_with"><mutation items="${
      node.elements.length
    }"></mutation>${itemBlocks.join('')}</block>`
  })

  registerHandler('MemberExpression', (code, node, walk) => {
    const objectBlock = walk.expr(code, node.object)
    if (node.property) {
      if (!node.computed) {
        const propertyName = node.property.name || node.property.value
        if (propertyName === 'length') {
          return `<block type="lists_length"><value name="VALUE">${objectBlock}</value></block>`
        }
        return `<block type="javascript_property_get"><value name="OBJECT">${objectBlock}</value><field name="PROPERTY">${propertyName}</field></block>`
      } else {
        const propertyBlock = walk.expr(code, node.property)
        return [
          `<block type="javascript_computed_property_get">`,
          `<value name="OBJECT">${objectBlock}</value>`,
          `<value name="PROPERTY">${propertyBlock}</value>`,
          `</block>`
        ].join('')
      }
    }
  })

  registerHandler('ReturnStatement', (code, node, walk) => {
    const argument = node.argument ? walk.expr(code, node.argument) : ''
    return `<block type="javascript_function_return"><value name="VALUE">${argument}</value></block>`
  })

  registerHandler('LogicalExpression', (code, node, walk) => {
    const left = walk.expr(code, node.left)
    const right = walk.expr(code, node.right)
    const op = node.operator === '||' ? 'OR' : 'AND'
    return `<block type="javascript_logic_operation"><field name="OP">${op}</field><value name="A">${left}</value><value name="B">${right}</value></block>`
  })

  registerHandler('UnaryExpression', (code, node, walk) => {
    const operator = node.operator
    const value = walk.expr(code, node.argument)
    return `<block type="javascript_unary_expression"><field name="OP">${operator}</field><value name="VALUE">${value}</value></block>`
  })

  registerHandler('ContinueStatement', (code, node, walk) => {
    return '<block type="controls_flow_statements"><field name="FLOW">CONTINUE</field></block>'
  })

  registerHandler('BreakStatement', (code, node, walk) => {
    return '<block type="controls_flow_statements"><field name="FLOW">BREAK</field></block>'
  })

  registerHandler('ThrowStatement', (code, node, walk) => {
    const throwArgument = node.argument
    let throwArgBlock = ''
    if (
      throwArgument.type === 'NewExpression' &&
      throwArgument.callee.type === 'Identifier' &&
      throwArgument.callee.name === 'Error'
    ) {
      throwArgBlock =
        throwArgument['arguments'].length > 0 ? walk.expr(code, throwArgument['arguments'][0]) : ''
    } else {
      throwArgBlock = walk.expr(code, throwArgument)
    }
    return `<block type="javascript_throw"><value name="MESSAGE">${throwArgBlock}</value></block>`
  })

  registerHandler('TryStatement', (code, node, walk) => {
    const body = walk.expr(code, node.block)
    const catchIdentifier = node.handler && node.handler.param && node.handler.param.name
    const catchBody = node.handler && node.handler.body && walk.expr(code, node.handler.body)
    const finalizerBody = node.finalizer && walk.expr(code, node.finalizer)
    const parts = _.compact([catchBody && 'catch', finalizerBody && 'finally'])
    return [
      `<block type="exceptions_try_catch">`,
      `<mutation parts="${parts.join(',')}"></mutation>`,
      `<field name="errorVariable">${catchIdentifier}</field>`,
      `<value name="tryPart">${body}</value>`,
      `<value name="catchPart">${catchBody}</value>`,
      `<value name="finallyPart">${finalizerBody}</value>`,
      `</block>`
    ].join('')
  })
}
